import { useCallback, useEffect, useRef, useState } from 'react';

const IDLE_TIMEOUT_MS = 4000;

const COMPACT_QUERY = "(max-width: 767px)";

function readIsCompact(): boolean {
  if (typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia(COMPACT_QUERY).matches;
}

/**
 * Keeps call overlays (header, controls) out of the way of the video: they show
 * on interaction and fade back out once the screen has been left alone.
 *
 * Only on a phone, where the video needs every pixel. A desktop has room for
 * both, and hiding the buttons there cost the AM team a day of thinking the
 * call was broken (CAD-6881). The breakpoint is answered here rather than left
 * to CSS, because the controls change shape when they are the last one
 * standing, and a class cannot decide that.
 */
export default function useOverlayVisibility(idleTimeoutMs: number = IDLE_TIMEOUT_MS) {
  const [isVisible, setIsVisible] = useState(true);
  const [isCompact, setIsCompact] = useState<boolean>(readIsCompact);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(function trackViewport() {
    if (typeof window.matchMedia !== 'function') {
      return undefined;
    }
    const matchCompact = window.matchMedia(COMPACT_QUERY);
    const onChange = () => setIsCompact(matchCompact.matches);

    onChange();
    matchCompact.addEventListener('change', onChange);

    return () => matchCompact.removeEventListener('change', onChange);
  }, []);

  const reveal = useCallback(() => {
    setIsVisible(true);
    clearTimeout(hideTimer.current);
    if (!isCompact) {
      return;
    }
    hideTimer.current = setTimeout(() => setIsVisible(false), idleTimeoutMs);
  }, [idleTimeoutMs, isCompact]);

  useEffect(function hideAfterInitialIdle() {
    reveal();
    return () => clearTimeout(hideTimer.current);
  }, [reveal]);

  return { isVisible, reveal };
}
